import { angleOf, dist, pt, type Pt } from '../model/geom';
import type { CadEntity } from '../model/entity';
import { normalizeAngle } from '../model/units';
import type { CadOp } from '../ops/operation';
import type { Command, CmdContext, CommandRunner, InputToken } from './types';

/**
 * 작성 명령 (작업지시서 P2-09)
 *   LINE · PLINE · RECTANGLE · CIRCLE · ARC · TEXT
 *
 * 별칭은 AutoCAD 기본 단축키를 그대로 쓴다. 손이 기억하는 것을 바꾸면 안 된다.
 */

function base(ctx: CmdContext, id: string): { id: string; layer: string } {
  return { id, layer: ctx.currentLayer };
}

/* ------------------------------------------------------------------ LINE */

export const LINE: Command = {
  name: 'LINE',
  aliases: ['L'],
  label: '선',
  hint: '두 점을 이어 선을 그립니다',
  *run(ctx: CmdContext): CommandRunner {
    const ops: CadOp[] = [];

    // 첫 점
    const first = yield { type: 'point', message: '첫 번째 점 지정' };
    if (first.t !== 'point') return;

    const firstPoint: Pt = first.p;
    let current: Pt = first.p;

    // 연속 입력 — AutoCAD 의 LINE 은 Enter 칠 때까지 계속 이어진다
    for (;;) {
      const start: Pt = current;
      const token: InputToken = yield {
        type: 'point',
        message: '다음 점 지정 (닫기 C / 명령 종료 Enter)',
        base: start,
        keywords: ['Close', 'Undo'],
        allowEnter: true,
        preview: (cursor) => [
          { ...base(ctx, 'preview'), type: 'line', a: start, b: cursor } as CadEntity,
        ],
      };

      if (token.t === 'enter' || token.t === 'esc') break;

      if (token.t === 'keyword') {
        if (token.k === 'Close' && ops.length > 0) {
          ops.push({
            kind: 'add',
            command: 'LINE',
            entities: [
              { ...base(ctx, ctx.newId()), type: 'line', a: start, b: firstPoint } as CadEntity,
            ],
          });
          break;
        }
        if (token.k === 'Undo' && ops.length > 0) {
          ops.pop();
          continue;
        }
        continue;
      }

      if (token.t !== 'point') continue;

      ops.push({
        kind: 'add',
        command: 'LINE',
        entities: [{ ...base(ctx, ctx.newId()), type: 'line', a: start, b: token.p } as CadEntity],
      });
      current = token.p;
    }

    return ops;
  },
};

/* ----------------------------------------------------------------- PLINE */

export const PLINE: Command = {
  name: 'PLINE',
  aliases: ['PL'],
  label: '폴리선',
  hint: '여러 점을 이어 하나의 선으로 만듭니다',
  *run(ctx: CmdContext): CommandRunner {
    const points: Pt[] = [];

    const first = yield { type: 'point', message: '시작점 지정' };
    if (first.t !== 'point') return;
    points.push(first.p);

    let closed = false;

    for (;;) {
      const snapshot = [...points];
      const token = yield {
        type: 'point',
        message: '다음 점 지정 (닫기 C / 되돌리기 U / 종료 Enter)',
        base: points[points.length - 1]!,
        keywords: ['Close', 'Undo'],
        allowEnter: true,
        preview: (cursor) => [
          {
            ...base(ctx, 'preview'),
            type: 'polyline',
            points: [...snapshot, cursor],
            closed: false,
          } as CadEntity,
        ],
      };

      if (token.t === 'enter' || token.t === 'esc') break;

      if (token.t === 'keyword') {
        if (token.k === 'Close' && points.length >= 3) {
          closed = true;
          break;
        }
        if (token.k === 'Undo' && points.length > 1) points.pop();
        continue;
      }

      if (token.t === 'point') points.push(token.p);
    }

    if (points.length < 2) return;

    return [
      {
        kind: 'add',
        command: 'PLINE',
        entities: [
          { ...base(ctx, ctx.newId()), type: 'polyline', points, closed } as CadEntity,
        ],
      },
    ];
  },
};

/* ------------------------------------------------------------ RECTANGLE */

export const RECTANGLE: Command = {
  name: 'RECTANGLE',
  aliases: ['REC', 'RECT'],
  label: '직사각형',
  hint: '두 모서리를 찍어 사각형을 그립니다',
  *run(ctx: CmdContext): CommandRunner {
    const a = yield { type: 'point', message: '첫 번째 구석 지정' };
    if (a.t !== 'point') return;

    const rectPts = (p1: Pt, p2: Pt): Pt[] => [
      p1,
      pt(p2.x, p1.y),
      p2,
      pt(p1.x, p2.y),
    ];

    const b = yield {
      type: 'point',
      message: '반대쪽 구석 지정',
      base: a.p,
      preview: (cursor) => [
        {
          ...base(ctx, 'preview'),
          type: 'polyline',
          points: rectPts(a.p, cursor),
          closed: true,
        } as CadEntity,
      ],
    };
    if (b.t !== 'point') return;
    if (b.p.x === a.p.x || b.p.y === a.p.y) {
      ctx.log('넓이가 0인 사각형은 만들 수 없습니다.');
      return;
    }

    return [
      {
        kind: 'add',
        command: 'RECTANGLE',
        entities: [
          {
            ...base(ctx, ctx.newId()),
            type: 'polyline',
            points: rectPts(a.p, b.p),
            closed: true,
          } as CadEntity,
        ],
      },
    ];
  },
};

/* ---------------------------------------------------------------- CIRCLE */

export const CIRCLE: Command = {
  name: 'CIRCLE',
  aliases: ['C'],
  label: '원',
  hint: '중심과 반지름으로 원을 그립니다',
  *run(ctx: CmdContext): CommandRunner {
    const c = yield { type: 'point', message: '중심점 지정' };
    if (c.t !== 'point') return;

    const r = yield {
      type: 'distance',
      message: '반지름 지정 (지름 D)',
      base: c.p,
      keywords: ['Diameter'],
      preview: (cursor) => [
        {
          ...base(ctx, 'preview'),
          type: 'circle',
          center: c.p,
          radius: Math.round(dist(c.p, cursor)),
        } as CadEntity,
      ],
    };

    let radius: number;
    if (r.t === 'distance') {
      radius = r.v;
    } else if (r.t === 'point') {
      radius = Math.round(dist(c.p, r.p));
    } else {
      return;
    }

    if (radius <= 0) {
      ctx.log('반지름은 0보다 커야 합니다.');
      return;
    }

    return [
      {
        kind: 'add',
        command: 'CIRCLE',
        entities: [
          { ...base(ctx, ctx.newId()), type: 'circle', center: c.p, radius } as CadEntity,
        ],
      },
    ];
  },
};

/* ------------------------------------------------------------------- ARC */

export const ARC: Command = {
  name: 'ARC',
  aliases: ['A'],
  label: '호',
  hint: '중심·시작·끝을 찍어 곡선을 그립니다',
  *run(ctx: CmdContext): CommandRunner {
    const c = yield { type: 'point', message: '중심점 지정' };
    if (c.t !== 'point') return;

    const s = yield {
      type: 'point',
      message: '시작점 지정',
      base: c.p,
      preview: (cursor) => [
        {
          ...base(ctx, 'preview'),
          type: 'circle',
          center: c.p,
          radius: Math.round(dist(c.p, cursor)),
        } as CadEntity,
      ],
    };
    if (s.t !== 'point') return;

    const radius = Math.round(dist(c.p, s.p));
    if (radius <= 0) {
      ctx.log('시작점이 중심과 같습니다.');
      return;
    }
    const startAngle = angleOf(c.p, s.p);

    const e = yield {
      type: 'point',
      message: '끝점 지정 (반시계 방향)',
      base: c.p,
      preview: (cursor) => [
        {
          ...base(ctx, 'preview'),
          type: 'arc',
          center: c.p,
          radius,
          startAngle,
          endAngle: angleOf(c.p, cursor),
        } as CadEntity,
      ],
    };
    if (e.t !== 'point') return;

    return [
      {
        kind: 'add',
        command: 'ARC',
        entities: [
          {
            ...base(ctx, ctx.newId()),
            type: 'arc',
            center: c.p,
            radius,
            startAngle,
            endAngle: normalizeAngle(angleOf(c.p, e.p)),
          } as CadEntity,
        ],
      },
    ];
  },
};

/* ------------------------------------------------------------------ TEXT */

export const TEXT: Command = {
  name: 'TEXT',
  aliases: ['T', 'DT'],
  label: '문자',
  hint: '도면에 글자를 씁니다',
  *run(ctx: CmdContext): CommandRunner {
    const at = yield { type: 'point', message: '문자 시작점 지정' };
    if (at.t !== 'point') return;

    const h = yield {
      type: 'distance',
      message: '글자 높이 지정',
      base: at.p,
      defaultValue: 200_000, // 200mm — 도면 주석 기본
    };
    const height = h.t === 'distance' ? h.v : h.t === 'enter' ? 200_000 : 0;
    if (height <= 0) return;

    const body = yield { type: 'text', message: '문자 입력' };
    if (body.t !== 'text' || body.s.trim() === '') return;

    return [
      {
        kind: 'add',
        command: 'TEXT',
        entities: [
          {
            ...base(ctx, ctx.newId()),
            type: 'text',
            at: at.p,
            text: body.s,
            height,
            rotation: 0,
            align: 'left',
          } as CadEntity,
        ],
      },
    ];
  },
};

export const DRAW_COMMANDS: Command[] = [LINE, PLINE, RECTANGLE, CIRCLE, ARC, TEXT];
